import React, { useEffect, useRef, useState } from 'react';
import { Button, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { IconFileImport } from '@tabler/icons-react';

import Localization from '../../Localization.jsx';
import {
  buildContentImportTemplate,
  contentImportTemplateFileName,
  formatContentImportIssue,
  parseContentImportWorkbook,
} from '../../tools/contentImportXlsx.js';

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const LIST_LIMIT = 12;

function apiRequest(root, cmd, body) {
  return new Promise((resolve, reject) => {
    root.refs.API.request(cmd, body, resolve, reject);
  });
}

function failureBody(res) {
  const body = (res && res.body) || {};
  if (Array.isArray(body.errors) && body.errors.length) {
    return body;
  }
  const message = body.remarks || '';
  return {
    errors: [{
      row_number: 0,
      code: 'request_failed',
      field: '',
      message,
      detail: { message },
    }],
    warnings: [],
    error_count: 1,
    warning_count: 0,
  };
}

function IssueList({ items, total, tone }) {
  const shown = items.slice(0, LIST_LIMIT);
  const extra = Math.max((total || items.length) - shown.length, 0);
  return (
    <Stack gap={4}>
      {shown.map((item, index) => (
        <Text key={index} size="sm" c={tone}>
          {formatContentImportIssue(Localization.strings, item)}
        </Text>
      ))}
      {extra > 0 && (
        <Text size="sm" c="dimmed">
          {Localization.strings.CONTENT_IMPORT_MORE.replace('{count}', extra)}
        </Text>
      )}
    </Stack>
  );
}

export default function ContentImportDialog({ opened, onClose, root, hotspots, localeName, epackId }) {
  const inputRef = useRef(null);
  // These update before React re-renders, so a second click cannot send the
  // same rows again while the first request is still leaving.
  const busyRef = useRef(false);
  const rowsRef = useRef([]);
  const checkGen = useRef(0);
  const [fileName, setFileName] = useState('');
  const [phase, setPhase] = useState('idle');
  const [fileIssue, setFileIssue] = useState(null);
  const [localIssues, setLocalIssues] = useState([]);
  const [serverBody, setServerBody] = useState(null);

  useEffect(() => {
    if (!opened) {
      checkGen.current += 1;
      busyRef.current = false;
      rowsRef.current = [];
      setFileName('');
      setPhase('idle');
      setFileIssue(null);
      setLocalIssues([]);
      setServerBody(null);
    }
  }, [opened]);

  const hotspotsReady = Array.isArray(hotspots) && hotspots.length > 0;
  const busy = phase === 'checking' || phase === 'importing';
  const preview = serverBody && Array.isArray(serverBody.preview) ? serverBody.preview : [];
  const serverErrors = serverBody && Array.isArray(serverBody.errors) ? serverBody.errors : [];
  const warnings = serverBody && Array.isArray(serverBody.warnings) ? serverBody.warnings : [];
  const canImport = phase === 'ready' && preview.length > 0 && serverErrors.length === 0 && !fileIssue && localIssues.length === 0;
  const showReport = !!(fileIssue || localIssues.length || serverErrors.length || canImport || warnings.length);

  function downloadTemplate() {
    const language = Localization.strings.getLanguage && Localization.strings.getLanguage();
    const bytes = buildContentImportTemplate(hotspots, language);
    const blob = new Blob([bytes], { type: XLSX_TYPE });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = contentImportTemplateFileName(localeName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  async function checkRows(rows, recheck, gen) {
    const ticket = recheck ? gen : (gen == null ? ++checkGen.current : gen);
    if (checkGen.current !== ticket) {
      return;
    }
    rowsRef.current = [];
    setPhase('checking');
    setServerBody(null);
    const localeId = Number(epackId);
    try {
      const res = await apiRequest(root, 'import_locale_content', {
        epack_id: localeId,
        dry_run: true,
        rows,
      });
      if (checkGen.current !== ticket) {
        return;
      }
      const data = (res && res.body && res.body.result) || {};
      rowsRef.current = rows;
      setServerBody({
        preview: data.preview || [],
        warnings: data.warnings || [],
        warning_count: data.warning_count || (data.warnings || []).length,
        errors: [],
        error_count: 0,
      });
      setPhase('ready');
    } catch (res) {
      if (checkGen.current !== ticket) {
        return;
      }
      const body = failureBody(res);
      if (!recheck && (body.errors || []).some((item) => item.code === 'counts_changed')) {
        return checkRows(rows, true, ticket);
      }
      rowsRef.current = [];
      setServerBody(body);
      setPhase('idle');
    }
  }

  async function handleFile(file) {
    if (!file || busyRef.current) {
      return;
    }
    const gen = ++checkGen.current;
    rowsRef.current = [];
    setFileName(file.name);
    setFileIssue(null);
    setLocalIssues([]);
    setServerBody(null);
    setPhase('checking');
    if (!/\.xlsx$/i.test(file.name)) {
      setFileIssue({ row_number: 0, code: 'not_xlsx', detail: {} });
      setPhase('idle');
      return;
    }
    let buffer;
    try {
      buffer = await file.arrayBuffer();
    } catch (e) {
      if (checkGen.current !== gen) {
        return;
      }
      setFileIssue({ row_number: 0, code: 'unreadable_file', detail: {} });
      setPhase('idle');
      return;
    }
    if (checkGen.current !== gen) {
      return;
    }
    const parsed = parseContentImportWorkbook(buffer);
    if (parsed.error) {
      setFileIssue(parsed.error);
      setPhase('idle');
      return;
    }
    if (parsed.localIssues.length) {
      setLocalIssues(parsed.localIssues);
      setPhase('idle');
      return;
    }
    await checkRows(parsed.requestRows, false, gen);
  }

  async function handleImport() {
    const rows = rowsRef.current;
    if (busyRef.current || !rows.length) {
      return;
    }
    busyRef.current = true;
    rowsRef.current = [];
    setPhase('importing');
    try {
      const res = await apiRequest(root, 'import_locale_content', {
        epack_id: Number(epackId),
        dry_run: false,
        rows,
      });
      const created = (res && res.body && res.body.result && res.body.result.created) || [];
      if (root && typeof root.addNotification === 'function') {
        root.addNotification(
          Localization.strings.CONTENT_IMPORT_DONE.replace('{count}', created.length),
          true,
        );
      }
      onClose();
    } catch (res) {
      busyRef.current = false;
      const body = failureBody(res);
      if ((body.errors || []).some((item) => item.code === 'counts_changed')) {
        await checkRows(rows);
        return;
      }
      setServerBody(body);
      setPhase('idle');
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={busy ? () => {} : onClose}
      title={<Text fw={600}>{Localization.strings.CONTENT_IMPORT_TITLE}</Text>}
      size="lg"
      centered
      zIndex={2000}
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      withCloseButton={!busy}
    >
      <Stack gap="md">
        <Text size="sm">{Localization.strings.CONTENT_IMPORT_INTRO}</Text>
        {!hotspotsReady && (
          <Text size="sm" c="dimmed">{Localization.strings.CONTENT_IMPORT_NEED_HOTSPOTS}</Text>
        )}
        <Group>
          <Button variant="default" onClick={downloadTemplate} disabled={!hotspotsReady || busy}>
            {Localization.strings.CONTENT_IMPORT_DOWNLOAD}
          </Button>
          <Button
            leftSection={<IconFileImport size={18} />}
            onClick={() => inputRef.current && inputRef.current.click()}
            disabled={!hotspotsReady || !epackId || busy}
          >
            {Localization.strings.CONTENT_IMPORT_CHOOSE}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files && event.target.files[0];
              event.target.value = '';
              handleFile(file);
            }}
          />
        </Group>
        {fileName ? <Text size="sm" c="dimmed">{fileName}</Text> : null}
        {phase === 'checking' && (
          <Group gap="sm">
            <Loader size={18} />
            <Text size="sm">{Localization.strings.CONTENT_IMPORT_CHECKING}</Text>
          </Group>
        )}
        {phase === 'importing' && (
          <Group gap="sm">
            <Loader size={18} />
            <Text size="sm">{Localization.strings.CONTENT_IMPORT_IMPORTING}</Text>
          </Group>
        )}
        {showReport && (
        <div style={{ maxHeight: 320, overflow: 'auto' }}>
        {fileIssue && (
          <Text size="sm" c="red">{formatContentImportIssue(Localization.strings, fileIssue)}</Text>
        )}
        {localIssues.length > 0 && (
          <IssueList items={localIssues} total={localIssues.length} tone="red" />
        )}
        {serverErrors.length > 0 && (
          <Stack gap={6}>
            <Text size="sm" fw={600}>
              {Localization.strings.CONTENT_IMPORT_ERROR_COUNT.replace('{count}', serverBody.error_count || serverErrors.length)}
            </Text>
            <IssueList items={serverErrors} total={serverBody.error_count || serverErrors.length} tone="red" />
          </Stack>
        )}
        {canImport && (
          <Stack gap={6}>
            <Text size="sm" fw={600}>
              {Localization.strings.CONTENT_IMPORT_READY.replace('{count}', preview.length)}
            </Text>
            <IssueList
              items={preview.map((item) => ({
                row_number: item.row_number,
                code: 'preview_line',
                message: '',
                detail: {
                  hotspot_name: item.hotspot_name || item.hotspot_id,
                  type: item.type,
                  title: item.title || '',
                },
              }))}
              total={preview.length}
              tone="dimmed"
            />
          </Stack>
        )}
        {warnings.length > 0 && serverErrors.length === 0 && localIssues.length === 0 && !fileIssue && (
          <Stack gap={6}>
            <Text size="sm" fw={600}>
              {Localization.strings.CONTENT_IMPORT_WARN_COUNT.replace('{count}', serverBody.warning_count || warnings.length)}
            </Text>
            <IssueList items={warnings} total={serverBody.warning_count || warnings.length} tone="orange" />
          </Stack>
        )}
        </div>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose} disabled={busy}>
            {Localization.strings.CANCEL}
          </Button>
          <Button onClick={handleImport} disabled={!canImport || busy}>
            {Localization.strings.CONTENT_IMPORT_CONFIRM}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
